import {
  ACTION,
  BlotterTableExtrasMenu,
  Button,
  ButtonVariants,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  Tour,
  useBlotterTableExtrasMenu,
  useMixpanel,
} from '@talos/kyoko';

import { useEmbeddedContext, useFeatureFlag, useRoleAuth } from '../../../hooks';

import { OMSView } from '@src/components/OMS/OMSView';
import { useAppStateDispatch } from '@src/providers/AppStateProvider';
import type { Order, UseBlotterTable } from '@talos/kyoko';
import { useMemo, type SetStateAction } from 'react';
import { openView } from '../../../components/OMS/OMSSlice';
import { TourName } from '../../../tokens/tours';
import { OrderSettingsToggles } from './OrderSettingsToggles';
import type { BlotterTableOrderFilter } from './useOrderFilter';

type OrderOptionsProps = {
  changeFilter: (action: SetStateAction<BlotterTableOrderFilter>) => void;
  filter: BlotterTableOrderFilter;
  handleCloneTab?: () => void;
  tabType: 'FilteredOrders' | 'OpenOrders';
  alwaysShowDDHOrders?: boolean;
  blotterTable: UseBlotterTable<Order>;
  handleExport?: () => void;
};

export function OrderOptions({
  filter,
  changeFilter,
  handleCloneTab,
  tabType,
  alwaysShowDDHOrders,
  handleExport,
  blotterTable,
}: OrderOptionsProps) {
  const mixpanel = useMixpanel();

  const { isAuthorized } = useRoleAuth();
  const dispatch = useAppStateDispatch();
  const { enableDynamicDeltaHedging } = useFeatureFlag();
  const { isEmbedded } = useEmbeddedContext();

  const handleImport = () => {
    mixpanel.track(MixpanelEvent.OpenOrderImportForm);
    dispatch(openView(OMSView.OrderImport));
  };

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleOnCloneTab: typeof handleCloneTab = useMemo(
    () =>
      handleCloneTab
        ? () => {
            mixpanel.track(MixpanelEvent.CloneTab);
            return handleCloneTab();
          }
        : undefined,
    [handleCloneTab, mixpanel]
  );

  // API orders toggle isn't hooked up to the orders provider which populated the OpenOrders blotter, so hide it
  const showAPIOrdersToggle = tabType !== 'OpenOrders';

  return (
    <div data-testid="order-options-menu">
      {enableDynamicDeltaHedging && !alwaysShowDDHOrders && <Tour tour={TourName.v249DDHOrderBlotterMenu} />}
      <BlotterTableExtrasMenu {...extrasMenuPopover}>
        <OrderSettingsToggles
          filter={filter}
          changeFilter={changeFilter}
          showAPIOrdersToggle={showAPIOrdersToggle}
          alwaysShowDDHOrders={alwaysShowDDHOrders}
          size={FormControlSizes.Small}
        />
        {handleOnCloneTab && !alwaysShowDDHOrders && (
          <Button
            startIcon={IconName.Duplicate}
            variant={ButtonVariants.Default}
            size={FormControlSizes.Small}
            onClick={handleOnCloneTab}
          >
            Clone Tab
          </Button>
        )}
        {isAuthorized(ACTION.SUBMIT_ORDER) && !isEmbedded && (
          <Button
            startIcon={IconName.DocumentUpload}
            variant={ButtonVariants.Default}
            size={FormControlSizes.Small}
            onClick={() => {
              extrasMenuPopover.close();
              handleImport();
            }}
          >
            Import Orders
          </Button>
        )}

        {tabType === 'OpenOrders' && (
          <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
        )}
        {tabType === 'FilteredOrders' && (
          <Button
            startIcon={IconName.DocumentDownload}
            size={FormControlSizes.Small}
            onClick={handleExport}
            data-testid="export-csv"
          >
            Export
          </Button>
        )}
      </BlotterTableExtrasMenu>
    </div>
  );
}
